<script setup>
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import 'moment/dist/locale/nl';;
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { ref, watch, computed } from 'vue'
import { Head, Link, usePage } from '@inertiajs/vue3'
import Paginator from 'primevue/paginator'

const props = defineProps({ userSearches: Array })
const deleteDialog = ref(false);
let deleteDialogText = "";
let deleteDialogUrl = "";
function openDeleteDialog(item) {
  deleteDialogText = item.name;
  deleteDialogUrl = "/portal/zoekopdracht/" + item.id + "/delete";
  deleteDialog.value = true;
}
const searchValue = ref('');
const page = usePage();
const paginator = ref(null);
const paginatedData = ref([]);
const pageUserSearches = computed(() => page.props.userSearches);
watch(pageUserSearches, function (userSearches) {
  if (paginator.value) {
    let currentPage = paginator.value.page;

    if (currentPage > Math.ceil(userSearches.length / 10)) {
      currentPage = Math.ceil(userSearches.length / 10);
    }
    props.userSearches = userSearches;
    changePagination({ first: 0, page: currentPage, pageCount: Math.ceil(userSearches.length / 10), rows: 10 });
  } else {
    props.userSearches = userSearches;
    changePagination({ first: 0, page: 0, pageCount: Math.ceil(userSearches.length / 10), rows: 10 });
  }
})
function changePagination(pageState) {
  if (pageState.page > pageState.pageCount) return;
  if (pageState.page < 0) return;
  const start = pageState.rows * pageState.page;
  const end = start + pageState.rows;
  if (searchValue.value.length < 3) {
    paginatedData.value = props.userSearches.slice(start, end);
  } else {
    paginatedData.value = props.userSearches.filter(dealer => {
      return dealer.name.toLowerCase().includes(searchValue.value.toLowerCase());
    }).slice(start, end);
  }
}
changePagination({ first: 0, page: 0, pageCount: Math.ceil(props.userSearches.length / 10), rows: 10 });
function search() {
  changePagination({ first: 0, page: 0, pageCount: Math.ceil(props.userSearches.length / 10), rows: 10 });
}
</script>

<template>
  <Head title="Zoekopdrachten overzicht | Mijnautosnelweg" />
  <main class="px-4 sm:px-6 py-6 space-y-8">
    <div>
      <nav class="sm:hidden" aria-label="Back">
        <Link href="/portal/dashboard" class="flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 gap-x-1">
            <font-awesome-icon :icon="['fas', 'chevron-left']" class="h-3 w-3 text-gray-400 shrink-0" />
            Terug
        </Link>
      </nav>
      <nav class="hidden sm:flex" aria-label="Breadcrumb">
        <ol role="list" class="flex items-center space-x-4">
          <li>
            <div class="flex">
              <Link href="/portal/dashboard" class="text-sm font-medium text-gray-500 hover:text-gray-700">Dashboard
              </Link>
            </div>
          </li>
          <font-awesome-icon :icon="['fas', 'chevron-right']" class="h-3 w-3 text-gray-400 shrink-0" />
          <li>
            <div class="flex items-center">
              <Link href="#" class="text-sm font-medium text-gray-900 hover:text-gray-700">
              Zoekopdrachten</Link>
            </div>
          </li>
        </ol>
      </nav>
    </div>
    <div class="flex flex-col sm:flex-row justify-between w-full space-y-2">
      <div class="flex flex-col">
        <h1 class="text-base font-semibold leading-6 text-gray-900">Zoekopdrachten</h1>
        <p class="mt-2 text-sm text-gray-700">Een overzicht van alle zoekopdrachten.</p>
      </div>
      <div class="flex flex-row gap-3 items-center">
        <IconField iconPosition="right" class="w-full sm:w-auto">
          <InputIcon class="pi pi-search"></InputIcon>
          <InputText class="h-10" placeholder="Search" />
        </IconField>
        <Link href="/portal/zoekopdracht/aanmaken" v-if="props.userSearches.length > 0">
            <font-awesome-icon :icon="['fal', 'plus']" class="w-5 h-5 p-2.5 bg-royalblue-500 hover:bg-royalblue-500/80 text-white shadow-sm rounded-lg shrink-0" aria-hidden="true" />
        </Link>
      </div>
    </div>
    <div class="space-y-8">
      <div class="mt-10 ring-1 ring-gray-300 rounded-lg w-full" v-if="props.userSearches.length > 0">
        <table class="min-w-full divide-y divide-gray-300 border-b">
          <thead>
            <tr>
              <th scope="col" class="px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                Naam
              </th>
              <th scope="col" class="px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                Merk &amp; model
              </th>
              <th scope="col"
                class="hidden sm:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                KM-stand </th>
              <th scope="col"
                class="hidden md:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                Bouwjaar </th>
              <th scope="col"
                class="hidden lg:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                Transmissie </th>
              <th scope="col"
                class="hidden lg:table-cell px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">
                Brandstof </th>
              <th scope="col" class="px-3 py-3.5 flex-1 text-left text-sm font-semibold text-gray-900">Richtprijs</th>
            </tr>
          </thead>
          <tbody class="divide-y">
            <tr v-for="userSearch in paginatedData" class="relative">
              <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 max-w-1 overflow-clip overflow-ellipsis">
                {{ userSearch.name ?? '' }}</td>
              <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 max-w-1 overflow-clip overflow-ellipsis">
                {{ userSearch.make ?? '' }} {{ userSearch.model ?? '' }} {{ userSearch.body_type ?? '' }}</td>
              <td class="hidden sm:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                {{ userSearch.mileage ?? '' }} tot {{ userSearch.mileage ?? '' }} km</td>
              <td class="hidden md:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                {{ userSearch.construction_year ??'' }} tot {{ userSearch.construction_year ?? '' }}</td>
              <td class="hidden lg:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                {{ userSearch.transmission ?? '' }}</td>
              <td class="hidden lg:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                {{ userSearch.fuel_type ?? '' }}</td>
              <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                €{{ userSearch.price ?? '' }} tot €{{ userSearch.price ?? '' }}</td>
              <td class="flex lg:hidden whitespace-nowrap py-4 text-sm gap-1.5 pr-3">
                <Link :href="'/portal/zoekopdracht/' + userSearch.id + '/edit'"
                  class="text-royalblue-500 hover:text-royalblue-500/80 ml-auto">
                <font-awesome-icon :icon="['far', 'pencil']" class="w-5 h-5 shrink-0" aria-hidden="true" />
                </Link> |
                <button @click="openDeleteDialog(userSearch)" class="text-royalblue-500 hover:text-royalblue-500/80">
                  <font-awesome-icon :icon="['far', 'trash-can']" class="w-5 h-5 shrink-0" aria-hidden="true" />
                </button>
              </td>
              <td class="hidden lg:flex whitespace-nowrap py-4 pr-4 pl-3 text-right text-sm font-medium gap-1.5">
                <Link :href="'/portal/zoekopdracht/' + userSearch.id + '/edit'"
                  class="text-royalblue-500 hover:text-royalblue-500/80 ml-auto">
                Wijzigen
                </Link>
                |
                <button @click="openDeleteDialog(userSearch)" class="text-royalblue-500 hover:text-royalblue-500/80">
                  Verwijderen
                </button>
              </td>
            </tr>
          </tbody>
        </table>

      </div>
      <div v-else>
        <div class="text-center">
          <font-awesome-icon :icon="['fal', 'magnifying-glass']" class="mx-auto h-12 w-12 text-gray-400 shrink-0" />
          <h3 class="mt-4 text-sm font-semibold text-gray-900">Geen zoekopdrachten</h3>
          <p class="mt-1 text-sm text-gray-500">Maak je eerste zoekopdracht aan.</p>
          <div class="mt-6">
            <Link href="/portal/zoekopdracht/aanmaken" type="button"
              class="inline-flex items-center rounded-md bg-royalblue-500 hover:bg-royalblue-500/80 px-3 py-2 text-sm font-semibold text-white shadow-sm  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
            Nieuwe zoekopdracht
            </Link>
          </div>
        </div>
      </div>

      <!--Pagination-->

      <Paginator ref="paginator" v-if="props.userSearches.length > 10" @page="changePagination" :rows="10"
        :totalRecords="props.userSearches.length" :rowsPerPageOptions="[10, 20, 30]"></Paginator>
    </div>
  </main>

  <!-- DELETE DIALOG -->
  <TransitionRoot as="template" :show="deleteDialog">
    <Dialog as="div" class="relative z-40" @close="deleteDialog = false">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
        leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed left-0 right-0 top-0 bottom-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed left-0 right-0 top-0 bottom-0 z-40 w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div class="sm:flex sm:items-start">
                <div
                  class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <font-awesome-icon :icon="['fal', 'triangle-exclamation']" class="h-6 w-6 text-red-500 shrink-0" />
                </div>
                <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Zoekopdracht verwijderen
                  </DialogTitle>
                  <div class="mt-2">
                    <p class="text-sm text-gray-500">Je staat op het punt om zoekopdracht <span
                        v-html="deleteDialogText"></span>
                      te verwijderen. Weet je dit zeker?</p>
                  </div>
                </div>
              </div>
              <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <Link preserve-scroll method="delete" :href="deleteDialogUrl" type="button"
                  class="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                  @click="deleteDialog = false" as="button">Verwijderen</Link>
                <button type="button"
                  class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  @click="deleteDialog = false" ref="cancelButtonRef">Annuleren</button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
  <!-- END DELETE DIALOG -->
</template>
